import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get, isObject, values } from 'lodash-es';
import type { FixingDetails } from '../../../types';
import { HStack, Stack } from '../../Core';
import { InlineFormattedNumber } from '../../FormattedNumber';
import { Text } from '../../Text';
import { Tooltip } from '../../Tooltip';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

type FixingPriceColumnValue = { Index: string; QuoteCurrency: string; Fixing?: string }[] | undefined;

/** Point this column at a FixingDetails object, and it will render the fixing indices and their provided prices */
export const fixingPrice: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueGetter: ({ data }: ValueGetterParams<unknown>): FixingPriceColumnValue => {
    if (!data || !column.field) {
      return undefined;
    }

    const fixingDetails: FixingDetails | undefined = get(data, column.field);
    if (!fixingDetails || !isObject(fixingDetails)) {
      return undefined;
    }

    if (fixingDetails.Index) {
      return [
        {
          Index: fixingDetails.Index,
          QuoteCurrency: 'USD', // we only do USD until FixingIndex type has a QuoteCurrency on it
          Fixing: fixingDetails.Fixing,
        },
      ];
    } else if (fixingDetails.MultilegFixingDetails) {
      return values(fixingDetails.MultilegFixingDetails).map(({ Index, Fixing }) => {
        return {
          Index,
          Fixing,
          QuoteCurrency: 'USD',
        };
      });
    }

    return undefined;
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, FixingPriceColumnValue>) => {
    if (!value) {
      return '';
    }

    if (value.length === 1) {
      return value.at(0)?.Fixing ?? '';
    }

    // For exports. User will see the output from the cell renderer in the app.
    // "BRRNY: 90150, ECX: -, XXRP: 1.0300"
    return value.map(entry => `${entry.Index}: ${entry.Fixing ?? '-'}`).join(', ');
  },
  cellRenderer: ({ value }: ICellRendererParams<unknown, FixingPriceColumnValue>) => {
    if (!value) {
      return null;
    }

    if (value.length === 1) {
      return <InlineFormattedNumber number={value.at(0)?.Fixing} currency={value.at(0)?.QuoteCurrency} />;
    }

    const providedFixings = value.filter(entry => entry.Fixing != null);
    return (
      <Tooltip
        tooltip={<FixingPriceTooltipContent value={value} />}
        tooltipTestID="fixing-price-tooltip-content"
        showUnderline
      >
        <Text>{`${providedFixings.length}/${value.length}`} Fixings</Text>
      </Tooltip>
    );
  },
});

const FixingPriceTooltipContent = ({ value }: { value: FixingPriceColumnValue }) => {
  return (
    <Stack gap="spacing2" minWidth="120px" data-testid="fixing-price-tooltip-content">
      {value?.map(({ Index, Fixing, QuoteCurrency }) => (
        <HStack key={Index} justifyContent="space-between" gap="spacing8">
          <Text>{Index}</Text>
          {Fixing ? <InlineFormattedNumber number={Fixing} currency={QuoteCurrency} /> : '-'}
        </HStack>
      ))}
    </Stack>
  );
};
